import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CreateProgram } from "./CreateProgram";
import { EditProgram } from "./EditProgram";
import { PricingProgramsTable } from "./PricingProgramsTable";
import { Button } from "../../core/basic/Button";
import { Breadcrumb, Breadcrumbs } from "../../core/basic/Breadcrumbs";
import { Stack } from "../../../../styled-system/jsx";
import { usePrograms } from "../../../modules/programs/api/usePrograms";
import { useActivatePricingPlanProgram } from "../../../modules/programs/api/useActivatePricingPlanProgram";
import { type PricingPlan } from "../../../zodios/apiDefinitions/pricing";

export type ProgramEditorView = { kind: "list" } | { kind: "create" } | { kind: "edit"; programId: number };

interface Props {
  pricingPlan: Pick<PricingPlan, "id" | "current_program">;
}

export const InlinePrograms = ({ pricingPlan }: Props) => {
  const { t } = useTranslation(["programs"]);
  const [view, setView] = useState<ProgramEditorView>({ kind: "list" });
  const showList = useCallback(() => setView({ kind: "list" }), []);
  const queryClient = useQueryClient();
  const title = t(($) => $.programs["Pricing Rules"]);

  const { data, isLoading } = usePrograms({ pricing_plan: pricingPlan.id });

  const { mutate: activate, isPending: isActivating, variables } = useActivatePricingPlanProgram(pricingPlan.id);
  const activeProgram = useMemo(
    () => ({
      // Optimistic while the save and the plan refetch are in flight.
      id: isActivating ? (variables ?? null) : (pricingPlan.current_program?.id ?? null),
      isPending: isActivating,
      onChange: activate,
    }),
    [isActivating, variables, pricingPlan.current_program?.id, activate],
  );

  const openProgram = useCallback((programId: number) => setView({ kind: "edit", programId }), []);

  if (view.kind === "list") {
    return (
      <PricingProgramsTable
        isLoading={isLoading}
        programs={data}
        title={title}
        persistKey="pricingPlanProgramsTable"
        onOpenProgram={openProgram}
        activeProgram={activeProgram}
        toolbarAction={
          <Button onPress={() => setView({ kind: "create" })}>{t(($) => $.programs.footer.Create)}</Button>
        }
      />
    );
  }

  return (
    <Stack gap="large">
      <Breadcrumbs>
        <Breadcrumb onPress={showList}>{title}</Breadcrumb>
        <Breadcrumb>
          {view.kind === "create" ? t(($) => $.programs.footer.Create) : t(($) => $.programs.Edit)}
        </Breadcrumb>
      </Breadcrumbs>
      {view.kind === "create" ?
        <CreateProgram
          pricingPlanId={pricingPlan.id}
          onSuccess={() => {
            showList();
            void queryClient.invalidateQueries({ queryKey: ["programs"] });
            // A plan's first program becomes its current one.
            void queryClient.invalidateQueries({ queryKey: ["pricing_plans"] });
            void queryClient.invalidateQueries({ queryKey: [{ entity: "pricing_plans" }] });
          }}
          onCancel={showList}
        />
      : <EditProgram
          programId={view.programId}
          onSuccess={() => {
            showList();
            void queryClient.invalidateQueries({ queryKey: ["programs"] });
          }}
          onCancel={showList}
        />
      }
    </Stack>
  );
};
